import React, {
   CSSProperties,
   forwardRef,
   ReactNode,
   Ref,
   useImperativeHandle,
   useRef
} from 'react';
import AntdCascader, { FilledFieldNamesType, ShowSearchType } from 'antd/lib/cascader'
import { DefaultValue } from '../select';

/**
 * {@docCategory Cascader}
 */
export interface CascaderOptionType {
   value?: string | number;
   label?: ReactNode;
   disabled?: boolean;
   isLeaf?: boolean;
   loading?: boolean;
   children?: Array<CascaderOptionType>;

   [key: string]: any;
}

export declare type CascaderValueType = (string | number)[];

/**
 * {@docCategory Cascader}
 */
export interface CascaderProps {

   /**
    * The id of the cascader component
    */
   id?: string;

   /**
    * Show clear icon
    * @defaultvalue true
    */
   allowClear?: boolean;

   /**
    * Disable component
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Whether has border style
    * @defaultvalue true
    */
   bordered?: boolean;

   /**
    * Size of cascader
    * @defaultvalue default
    */
   size?: 'small' | 'default' | 'large';

   /**
    * Placeholder text
    */
   placeholder?: string;

   /**
    * The data options of the cascader.
    */
   options?: Array<CascaderOptionType>;

   /**
    * The selected value.
    */
   value?: CascaderValueType;

   /**
    * The render function to display selected options.
    * @defaultvalue label =\> label.join(/)
    */
   displayRender?: (label: string[], selectedOptions?: CascaderOptionType[]) => ReactNode;

   /**
    * Get focus by default
    * @defaultvalue false
    */
   autoFocus?: boolean;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback when finishing selecting
    */
   onChange?: (value: CascaderValueType, selectedOptions?: CascaderOptionType[]) => void;
}

function highlightKeyword(str: string, keyword: string, prefixCls: string | undefined) {
   return str.toLowerCase().split(keyword.toLowerCase()).map((node: string, index: number) =>
                                    index === 0
                                    ? node
                                    : [
                                          <span className={`${prefixCls}-menu-item-keyword`} key="seperator">
                                             {keyword}
                                          </span>,
                                          node,
                                       ],
   );
}

const showSearch: ShowSearchType = {
   filter: (inputValue: string,
            path: CascaderOptionType[],
            names: FilledFieldNamesType): boolean => {
      return path.some(option => {
         return (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1;
      });
   },
   render: (inputValue: string,
            path: CascaderOptionType[],
            prefixCls: string | undefined,
            names: FilledFieldNamesType): ReactNode => {
      return path.map((option, index) => {
         const label = option[names.label] as string;
         const node = highlightKeyword(label, inputValue, prefixCls);
         return index === 0 ? node : [' / ', node];
      });
   },
   sort: (a: CascaderOptionType[],
          b: CascaderOptionType[],
          inputValue: string,
          names: FilledFieldNamesType): number => {
      function callback(elem: CascaderOptionType) {
         return (elem[names.label] as string).toLowerCase().indexOf(inputValue) > -1;
      }

      return a.findIndex(callback) - b.findIndex(callback);
   }
};

export interface CascaderRefProps {
   focus: () => void;
   blur: () => void;
}

export const Cascader = forwardRef(function Cascader(props: CascaderProps, ref: Ref<CascaderRefProps>) {

   const antdCascaderRef = useRef<any>(null);

   useImperativeHandle(ref, () => ({
      focus: () => {
         if (antdCascaderRef && antdCascaderRef.current) {
            antdCascaderRef.current.focus();
         }
      },
      blur: () => {
         if (antdCascaderRef && antdCascaderRef.current) {
            antdCascaderRef.current.blur();
         }
      }
   }));

   return (
      <AntdCascader
         id={props.id}
         ref={antdCascaderRef}
         allowClear={props.allowClear}
         disabled={props.disabled}
         bordered={props.bordered}
         size={props.size == 'default' ? 'middle' : props.size}
         placeholder={props.placeholder}
         options={props.options}
         value={props.value}
         displayRender={props.displayRender}
         showSearch={showSearch}
         autoFocus={props.autoFocus}
         className={props.className}
         style={props.style}
         onChange={props.onChange}
      />
   );
});
